import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Badge, Modal, Select, LoadingSpinner, ErrorSummary } from '@/components/common';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '@/services/api';
import toast from 'react-hot-toast';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';

interface InsuranceType {
  id: string;
  name: string;
}

interface PolicyType {
  id: string;
  insurance_type: string;
  insurance_type_name: string;
  name: string;
  code: string;
  description: string;
  is_active: boolean;
}

const initialForm = { insurance_type: '', name: '', description: '', is_active: true };

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export function PolicyTypesPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Global rule 1 -- search-as-you-type without a request per keystroke.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  // Item 9: Insurance Type and Status were table columns with no way to
  // narrow by them; both go straight to `PolicyTypeViewSet`, whose
  // `filterset_fields` already includes `insurance_type` and `is_active` (and
  // `search_fields` covers name/code) -- this page just never sent them.
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formData, setFormData] = useState(initialForm);
  const [descriptionError, setDescriptionError] = useState('');

  const {
    data: policyTypes = [],
    isLoading,
    isError,
    error,
  } = useQuery<PolicyType[]>({
    queryKey: ['policy-types', debouncedSearch, typeFilter, statusFilter],
    queryFn: async () => {
      const params: Record<string, string> = { page_size: '200' };
      if (debouncedSearch) params.search = debouncedSearch;
      if (typeFilter) params.insurance_type = typeFilter;
      if (statusFilter) params.is_active = statusFilter;
      const res = await api.get('/products/policy-types/', { params });
      return res.data.results || res.data;
    },
  });

  const { data: types = [] } = useQuery<InsuranceType[]>({
    queryKey: ['insurance-types-lookup'],
    queryFn: async () => {
      const res = await api.get('/products/types/', { params: { page_size: 200 } });
      return res.data.results || res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof initialForm) => {
      if (editingId) {
        const res = await api.patch(`/products/policy-types/${editingId}/`, data);
        return res.data;
      }
      const res = await api.post('/products/policy-types/', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy-types'] });
      setShowModal(false);
      setFormData(initialForm);
      setEditingId(null);
      toast.success(editingId ? 'Policy type updated' : 'Policy type created');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/products/policy-types/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy-types'] });
      toast.success('Policy type deleted');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handleEdit = (policyType: PolicyType) => {
    setEditingId(policyType.id);
    setFormData({
      insurance_type: policyType.insurance_type,
      name: policyType.name,
      description: policyType.description,
      is_active: policyType.is_active,
    });
    setDescriptionError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialForm);
    setDescriptionError('');
  };

  const typeOptions = types.map((t) => ({ value: t.id, label: t.name }));
  const typeFilterOptions = [{ value: '', label: 'All insurance types' }, ...typeOptions];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Policy Types</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage policy types</p>
        </div>
        <Button variant="primary" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setShowModal(true)}>
          Add Policy Type
        </Button>
      </div>

      <Card padding="md">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Input
              placeholder="Search policy types..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <Select
            options={typeFilterOptions}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          />
          <Select
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          />
        </div>
      </Card>

      {isError && <ErrorSummary messages={[getErrorMessage(error)]} />}

      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          // A failed request is not "there are none" -- three distinct states,
          // not two. The ErrorSummary above already names the problem; this is
          // just the table area not pretending to have an answer.
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Could not load policy types</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Try again in a moment.</p>
          </div>
        ) : policyTypes.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No policy types found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Get started by adding a policy type.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Insurance Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {policyTypes.map((policyType) => (
                  <tr key={policyType.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{policyType.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{policyType.description || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{policyType.insurance_type_name || '-'}</td>
                    <td className="px-6 py-4">
                      <Badge variant={policyType.is_active ? 'success' : 'warning'}>
                        {policyType.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(policyType)}>
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(policyType.id)}>
                          <TrashIcon className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal isOpen={showModal} onClose={handleCloseModal} title={editingId ? 'Edit Policy Type' : 'Add Policy Type'}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!formData.description.trim()) {
              setDescriptionError('Description is required');
              return;
            }
            setDescriptionError('');
            createMutation.mutate(formData);
          }}
          className="space-y-4"
        >
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Description
              <span className="text-red-500 dark:text-red-400 ml-1">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                if (descriptionError) setDescriptionError('');
              }}
              rows={3}
              required
              className="w-full px-3 py-2 border rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-gray-300 hover:border-gray-400 bg-white dark:bg-gray-700 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 dark:border-gray-600 dark:hover:border-gray-500 dark:focus:ring-primary-400"
            />
            {descriptionError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{descriptionError}</p>}
          </div>
          <Select
            label="Insurance Type"
            options={typeOptions}
            value={formData.insurance_type}
            onChange={(e) => setFormData({ ...formData, insurance_type: e.target.value })}
            placeholder="Select an insurance type"
            required
          />
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</span>
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" isLoading={createMutation.isPending}>{editingId ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default PolicyTypesPage;
